import React, { useContext, useState, useRef } from 'react'
import { Grant, GrantStatus } from '../../../../generated/graphql'
import { formatNumber, getCurrency, getSymbolOfCurrency } from '../../../../utils/CurrencyFormatter'
import { GlobalContext } from '../../store/context/globalContext'
import ExpiryAnalysisCard from './ExpiryAnalysisCard'
import { PortfolioOverviewCard } from './PortfolioOverViewCard'
import CompanyCard from './CompanyCard'

type Props = {
  grants: Grant[]
  onClickAdd?: () => void
}


const generateDynamicRangeOptions = (grants: Grant[]) => {
  const today = new Date()

  const activeGrantsWithExpiry = grants.filter((grant) => {
    return (
      grant.status === GrantStatus.InActive &&
      grant.expiryDate &&
      new Date(grant.expiryDate) > today
    )
  })

  if (activeGrantsWithExpiry.length === 0) {
    return [
      { value: 3, label: '3 Months' },
      { value: 6, label: '6 Months' },
      { value: 12, label: '1 Year' },
      { value: 24, label: '2 Years' },
      { value: 36, label: '3 Years' },
    ]
  }

  const sortedByExpiry = activeGrantsWithExpiry.sort(
    (a, b) => new Date(a.expiryDate!).getTime() - new Date(b.expiryDate!).getTime()
  )

  const latestExpiryDate = new Date(sortedByExpiry[sortedByExpiry.length - 1]?.expiryDate!)
  const monthsToLatestExpiry = Math.ceil(
    (latestExpiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24 * 30)
  )

  const ranges = []

  if (monthsToLatestExpiry >= 3) ranges.push({ value: 3, label: '3 Months' })
  if (monthsToLatestExpiry >= 6) ranges.push({ value: 6, label: '6 Months' })
  if (monthsToLatestExpiry >= 12) ranges.push({ value: 12, label: '1 Year' })
  if (monthsToLatestExpiry >= 18) ranges.push({ value: 18, label: '18 Months' })
  if (monthsToLatestExpiry >= 24) ranges.push({ value: 24, label: '2 Years' })
  if (monthsToLatestExpiry >= 36) ranges.push({ value: 36, label: '3 Years' })
  if (monthsToLatestExpiry >= 48) ranges.push({ value: 48, label: '4 Years' })
  if (monthsToLatestExpiry >= 60) ranges.push({ value: 60, label: '5 Years' })

  const maxRangeMonths = Math.min(Math.ceil(monthsToLatestExpiry / 6) * 6, 120)
  if (maxRangeMonths > (ranges[ranges.length - 1]?.value || 0)) {
    const years = Math.floor(maxRangeMonths / 12)
    const remainingMonths = maxRangeMonths % 12
    let label = ''
    if (years > 0) {
      label += `${years} Year${years > 1 ? 's' : ''}`
      if (remainingMonths > 0) {
        label += ` ${remainingMonths} Month${remainingMonths > 1 ? 's' : ''}`
      }
    } else {
      label = `${maxRangeMonths} Month${maxRangeMonths > 1 ? 's' : ''}`
    }
    ranges.push({ value: maxRangeMonths, label })
  }

  return ranges.length > 0 ? ranges : [{ value: 12, label: '1 Year' }]
}

const calculateExpiryAnalysis = (grants: Grant[], expiryRangeMonths: number = 12) => {
  const today = new Date()

  const activeGrantsWithExpiry = grants.filter((grant) => {
    return (
      grant.status === GrantStatus.InActive &&
      grant.expiryDate &&
      new Date(grant.expiryDate) > today
    )
  })

  if (activeGrantsWithExpiry.length === 0) {
    return {
      totalUnrealizedValue: 0,
      nextExpiryDate: null,
      expiryWindow: null,
      totalExpiringValue: 0,
      potentialLoss: 0,
      earliestExpiry: null,
      latestExpiry: null,
    }
  }

  const totalUnrealizedValue = activeGrantsWithExpiry.reduce((sum, grant) => {
    const fmv = grant.companyFMV || 0
    const vestedOptions = grant.vestedOptions || 0
    return sum + vestedOptions * fmv
  }, 0)

  const sortedByExpiry = activeGrantsWithExpiry.sort(
    (a, b) => new Date(a.expiryDate!).getTime() - new Date(b.expiryDate!).getTime()
  )

  const nextExpiryDate = sortedByExpiry[0]?.expiryDate
  const earliestExpiry = sortedByExpiry[0]?.expiryDate
  const latestExpiry = sortedByExpiry[sortedByExpiry.length - 1]?.expiryDate

  const expiryDateFromNow = new Date()
  expiryDateFromNow.setMonth(today.getMonth() + expiryRangeMonths)

  const expiringWithinRange = activeGrantsWithExpiry.filter((grant) => {
    return new Date(grant.expiryDate!) <= expiryDateFromNow
  })

  const totalExpiringValue = expiringWithinRange.reduce((sum, grant) => {
    const fmv = grant.companyFMV || 0
    const vestedOptions = grant.vestedOptions || 0
    return sum + vestedOptions * fmv
  }, 0)

  return {
    totalUnrealizedValue,
    nextExpiryDate,
    earliestExpiry,
    latestExpiry,
    totalExpiringValue,
    potentialLoss: totalExpiringValue,
  }
}

const formatCurrency = (amount: number, userCurrency: string): string => {
  const userCurrencyFormat = getCurrency(userCurrency)
  const currencySymbol = getSymbolOfCurrency(userCurrency)

  return `${currencySymbol}${formatNumber({
    value: amount,
    format: userCurrencyFormat,
  })}`
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
  })
}

const formatDateRange = (startDate: string | null, endDate: string | null): string => {
  if (!startDate || !endDate) return '-'

  const start = new Date(startDate)
  const end = new Date(endDate)

  const startMonth = start.toLocaleDateString('en-US', { month: 'short' })
  const endMonth = end.toLocaleDateString('en-US', { month: 'short' })
  const year = end.getFullYear()

  if (start.getFullYear() === end.getFullYear()) {
    return `${startMonth} - ${endMonth} ${year}`
  } else {
    return `${startMonth} ${start.getFullYear()} - ${endMonth} ${year}`
  }
}


export const EsopSummaryCards: React.FC<Props> = ({ grants, onClickAdd }) => {
  const { state } = useContext(GlobalContext)
  const { userCurrency } = state
  const companyCardRef = useRef<{ scrollToCard: () => void }>(null) // Ref for CompanyCard
  const [editGrant, setEditGrant] = useState<Grant | null>(null) // State for editing grant
  const [showEditModal, setShowEditModal] = useState(false) // State for edit modal

  const dynamicRangeOptions = generateDynamicRangeOptions(grants)

  const getSelectedRangeLabel = () => {
    const found = dynamicRangeOptions.find((r) => r.value === selectedExpiryRange)
    if (found) return found.label
    if (selectedExpiryRange % 12 === 0) {
      const years = selectedExpiryRange / 12
      return years === 1 ? '1 Year' : `${years} Years`
    }
    if (selectedExpiryRange % 1 === 0 && selectedExpiryRange < 12) {
      return `${selectedExpiryRange} Month${selectedExpiryRange > 1 ? 's' : ''}`
    }
    return `${selectedExpiryRange * 30} Days`
  }

  const [selectedExpiryRange, setSelectedExpiryRange] = useState(() => {
    const defaultValue = dynamicRangeOptions[0]?.value || 12
    return dynamicRangeOptions.some((r) => r.value === defaultValue)
      ? defaultValue
      : dynamicRangeOptions[0]?.value || 12
  })

  const expiryData = calculateExpiryAnalysis(grants, selectedExpiryRange)

  const handleExpiryRangeChange = (months: number) => {
    if (dynamicRangeOptions.some((r) => r.value === months)) {
      setSelectedExpiryRange(months)
    }
  }

  const handleAddFMVClick = () => {
    if (companyCardRef.current) {
      companyCardRef.current.scrollToCard()
      const grantWithMissingFMV = grants.find((grant) => !grant.companyFMV)
      if (grantWithMissingFMV) {
        setEditGrant(grantWithMissingFMV)
        setShowEditModal(true)
      }
    }
  }

  const totalCompanies = new Set(grants.map((g) => g.companyName)).size
  const totalGrants = grants.length
  const totalVested = grants.reduce((sum, g) => sum + (g.vestedOptions || 0), 0)

  const isPartialData = grants.some((grant) => grant.companyFMV === 0)

  const grantsByCompany = grants.reduce((acc, grant) => {
    const companyName = grant.companyName || 'Unknown Company'
    if (!acc[companyName]) {
      acc[companyName] = []
    }
    acc[companyName].push(grant)
    return acc
  }, {} as Record<string, Grant[]>)

  return (
    <div className='d-flex flex-column gap-8 mb-8'>
      <PortfolioOverviewCard grants={grants} />
      <ExpiryAnalysisCard
        grants={grants}
        selectedRange={selectedExpiryRange}
        onExpiryRangeChange={handleExpiryRangeChange}
        selectedRangeLabel={getSelectedRangeLabel()}
        onAddFMVClick={onClickAdd}
      />
    </div>
  )
}
